'use client';
/**
 * nav-live — 헤더 LIVE 카운터: 「[● LIVE] 오늘 N회」 + 「30일 누적 M회」 — 조회수(행 수 · 봇 제외). 순 방문자가 아니다(P1 정정 · 키 의미는 lib/nav/visitor-count).
 *
 *   · 서버 HTML·첫 수화 = 고정 폭 빈 자리(숫자 없음) — 방문자·UA 무관 같은 HTML · 쪽을 동적으로 만들지 않는다.
 *   · 수화 뒤 GET /api/visitors/pv30d(CDN 60초 캐시)로 숫자를 채운다. 자리 폭은 CSS 로 고정 → 채워져도 시프트 0.
 *   · 값 = 굴러가는 30일 사람 페이지뷰(반복 조회 포함) 실측 그대로 — 부풀림·바닥값 0. 값이 없으면 빈 자리 그대로.
 *   · 두 줄(「최근 30일 조회」 / 「N회」)로 접어 360px 에서도 낱말을 숨기지 않는다(nav-v2.css 실측 주석).
 *   · 탭이 보일 때만 2분마다 다시 묻는다(가려지면 쉼).
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import { useEffect, useState } from 'react';
import { LiveBadge } from '@/components/live/LiveBadge';
import { parseVisitors, visitorView, type VisitorView } from '@/lib/nav/visitor-count';

export const VISITOR_POLL_MS = 20_000; // 10/26 「실시간」 — 처음 5분은 20초마다(탭이 보일 때만)
/** 5분 뒤에는 60초마다 — 오래 열어 둔 탭이 엣지 요청을 쌓지 않게(검토 10/26: 탭당 하루 4,320회 → 약 1,500회). */
export const VISITOR_POLL_SLOW_MS = 60_000;
export const VISITOR_FAST_WINDOW_MS = 5 * 60_000;
export const VISITORS_API = '/api/visitors/pv30d';

export function VisitorCountView({ view }: { view: VisitorView | null }) {
  if (!view) return <span className="kd-vc" data-nav-visitors="pending" aria-hidden="true" />;
  return (
    <span className="kd-vc" data-nav-visitors="live" role="status" aria-label={view.label} title={view.label}>
      {/* live-v3(2026-11-02 · A안): 「[● LIVE] 오늘 678명」 위 · 「30일 누적 8,732명」 아래(자리 136px 안 · 360px 포함). */}
      <span className="kd-vc__t" aria-hidden="true">
        <span className="kd-vc__l1"><LiveBadge className="kd-vc__live" /><span className="kd-vc__lb">오늘</span><b className="kd-vc__n">{view.today}</b><span className="kd-vc__u">회</span></span>
        <span className="kd-vc__k">30일 누적 {view.cum}회</span>
      </span>
    </span>
  );
}

export default function NavVisitorCount() {
  const [view, setView] = useState<VisitorView | null>(null);
  useEffect(() => {
    let alive = true;
    let last = 0;
    let shown: number | null = null;
    const run = async () => {
      if (document.hidden || Date.now() - last < VISITOR_POLL_MS / 2) return; // 빠른 주기 기준 최소 간격
      last = Date.now();
      try {
        const r = await fetch(VISITORS_API);
        const j = r.ok ? await r.json() : null;
        if (!(alive && j && j.ok === true)) return;
        const counts = parseVisitors(j);
        if (!counts) return;
        const next = counts.today;
        // 10/26 「실시간」 — 오늘 숫자가 오르면 1초 동안 굴려 올린다(움직임 줄이기 설정이면 바로). 내려가면(자정) 바로.
        const from = shown;
        shown = next;
        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (typeof from !== 'number' || next <= from || reduce || typeof requestAnimationFrame !== 'function') { setView(visitorView(counts)); return; }
        const t0 = performance.now();
        const step = (t: number) => {
          if (!alive) return;
          const k = Math.min(1, (t - t0) / 1000);
          setView(visitorView({ today: Math.round(from + (next - from) * k), cum: counts.cum }));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      } catch { /* 실패 — 자리는 비운 채 다음 주기에 다시 */ }
    };
    void run();
    let t = window.setInterval(run, VISITOR_POLL_MS);
    const slow = window.setTimeout(() => { window.clearInterval(t); t = window.setInterval(run, VISITOR_POLL_SLOW_MS); }, VISITOR_FAST_WINDOW_MS);
    document.addEventListener('visibilitychange', run);
    return () => { alive = false; window.clearInterval(t); window.clearTimeout(slow); document.removeEventListener('visibilitychange', run); };
  }, []);
  return <VisitorCountView view={view} />;
}
